import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { segmentHowRelayWorks } from "../lib/howRelayWorksLinks";

/**
 * Open a How Relay Works panel in-app by setting the hash (App's hashchange
 * handler does the rest). If the hash already equals the slug (the panel was
 * dismissed without clearing it), assigning it fires no event, so dispatch
 * hashchange by hand to open the panel again.
 */
export function openHowRelayWorksSlug(slug: string): void {
  if (window.location.hash === `#${slug}`) {
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  } else {
    window.location.hash = slug;
  }
}

const LINK_STYLE = {
  color: "var(--er-accent)",
  textDecoration: "underline",
  cursor: "pointer",
} as const;

/** An in-app link to a How Relay Works panel. */
export function HowRelayWorksLink({
  slug,
  children,
  ...anchorProps
}: { slug: string; children: ReactNode } & Omit<ComponentPropsWithoutRef<"a">, "href" | "onClick" | "style">) {
  return (
    <a
      {...anchorProps}
      className={["hrw-text-link", anchorProps.className].filter(Boolean).join(" ")}
      href={`/#${slug}`}
      onClick={(e) => {
        e.preventDefault();
        openHowRelayWorksSlug(slug);
      }}
      style={LINK_STYLE}
    >
      {children}
    </a>
  );
}

/** Plain text with How Relay Works /#slug links rendered as in-app links. */
export function HowRelayWorksText({ text }: { text: string }) {
  return (
    <>
      {segmentHowRelayWorks(text).map((seg, i) =>
        typeof seg === "string" ? seg : (
          <HowRelayWorksLink key={i} slug={seg.slug}>
            {seg.label}
          </HowRelayWorksLink>
        ),
      )}
    </>
  );
}
